/**
 * Client-portal API client (SPEC E4 — client-portal, R3).
 *
 * The portal CONSUMES existing tenant-scoped endpoints — it adds no new data
 * endpoints. A CLIENT-role user's Firebase token scopes every read to their own
 * tenant via lib/tenant_scope on the backend, so these calls return only the
 * client's data.
 *
 * Reads:
 *   - Leads    → GET /api/prospects                 (tenant-scoped lead list)
 *   - Pipeline → GET /api/pipeline/summary + /definitions (E3 + E6 pipeline def)
 *   - Results  → GET /api/prospects + /api/pipeline/summary (counts + weighted)
 *   - Columns  → GET /api/tenant-display/field-config/{surface}  (E6 columns)
 *   - Branding is delivered inline on /api/me (no fetch here).
 *   - Conversations: NOT wired — the view degrades to a "coming soon" placeholder
 *     (E1c unified inbox not built yet; additive — see PortalConversations).
 *
 * Follows the fetch + Firebase-ID-token auth pattern from lib/api/prospects.ts.
 */

import { auth } from '../firebase'

const API_BASE_URL =
  import.meta.env.VITE_API_URL ||
  'https://sales-api-768596480524.us-central1.run.app'

async function getAuthHeader(): Promise<Record<string, string>> {
  const user = auth.currentUser
  if (!user) throw new Error('Not authenticated')
  const token = await user.getIdToken()
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  }
}

async function handle<T>(response: Response, what: string): Promise<T> {
  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ detail: response.statusText }))
    throw new Error(error.detail || `Failed to ${what}: ${response.statusText}`)
  }
  return response.json()
}

// ---------------------------------------------------------------------------
// Types (loose — the portal reads a subset of each endpoint's payload)
// ---------------------------------------------------------------------------

export interface PortalLead {
  id: string
  name?: string
  phone?: string
  email?: string
  status?: string
  last_call_outcome?: string
  next_call_date?: string
  custom_fields?: Record<string, unknown>
  [k: string]: unknown
}

export interface PortalLeadsResponse {
  prospects: PortalLead[]
  total: number
}

export interface PortalPipelineStage {
  stage_key: string
  display_label: string
  color?: string
  ordinal?: number
  is_terminal?: boolean
  count?: number
  total_value?: number
}

export interface PortalPipelineSummary {
  stages: Array<{
    stage_key: string
    count?: number
    total_value?: number
    weighted_value?: number
  }>
  total_value?: number
  weighted_total?: number
  [k: string]: unknown
}

export interface PortalPipelineDefinition {
  pipeline_id: string
  name: string
  stages: PortalPipelineStage[]
}

/** A resolved column descriptor (from E6 field-config or a hardcoded default). */
export interface PortalColumn {
  field_key: string
  label: string
}

export interface FieldConfigResponse {
  configured: boolean
  surface: string
  field_config?: {
    columns: Array<{
      field_key: string
      label?: string
      label_override?: string
      visible?: boolean
      display_order?: number
    }>
  }
}

// ---------------------------------------------------------------------------
// Calls
// ---------------------------------------------------------------------------

/** GET /api/prospects — the client's tenant-scoped leads (read-only). */
export async function getPortalLeads(limit = 100): Promise<PortalLeadsResponse> {
  const headers = await getAuthHeader()
  const response = await fetch(
    `${API_BASE_URL}/api/prospects?limit=${limit}&queue=all`,
    { headers }
  )
  return handle<PortalLeadsResponse>(response, 'load leads')
}

/** GET /api/pipeline/summary — per-stage counts / value / weighted total. */
export async function getPortalPipelineSummary(): Promise<PortalPipelineSummary> {
  const headers = await getAuthHeader()
  const response = await fetch(`${API_BASE_URL}/api/pipeline/summary`, { headers })
  return handle<PortalPipelineSummary>(response, 'load pipeline summary')
}

/** GET /api/pipeline/definitions — the resolved pipeline (stage labels/colors). */
export async function getPortalPipelineDefinition(): Promise<{
  pipeline: PortalPipelineDefinition
}> {
  const headers = await getAuthHeader()
  const response = await fetch(`${API_BASE_URL}/api/pipeline/definitions`, {
    headers,
  })
  return handle<{ pipeline: PortalPipelineDefinition }>(
    response,
    'load pipeline definition'
  )
}

/**
 * GET /api/tenant-display/field-config/{surface} — the E6 column config for a
 * portal surface. Returns `{configured:false}` (200) when the tenant has no
 * config, which the caller resolves to its hardcoded default columns
 * (degradation contract E4-a).
 */
export async function getSurfaceFieldConfig(
  surface: string
): Promise<FieldConfigResponse> {
  const headers = await getAuthHeader()
  const response = await fetch(
    `${API_BASE_URL}/api/tenant-display/field-config/${encodeURIComponent(surface)}`,
    { headers }
  )
  return handle<FieldConfigResponse>(response, 'load field config')
}

/**
 * Resolve the columns a surface should render: the E6-configured columns when a
 * config exists (visible only, ordered, custom-field labels resolved), else the
 * provided hardcoded defaults (degradation E4-a). Pure given the fetched config.
 */
export function resolveColumns(
  config: FieldConfigResponse,
  defaultColumns: PortalColumn[]
): PortalColumn[] {
  if (!config.configured || !config.field_config?.columns?.length) {
    return defaultColumns
  }
  const defaultLabelByKey = new Map(
    defaultColumns.map((c) => [c.field_key, c.label])
  )
  return config.field_config.columns
    .filter((c) => c.visible !== false)
    .sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0))
    .map((c) => ({
      field_key: c.field_key,
      label:
        c.label_override ||
        c.label ||
        defaultLabelByKey.get(c.field_key) ||
        c.field_key,
    }))
}
